/**
 * Integrytas Group - Base App (MOBILE)
 * Page: DAP.POLICIES — mobile-native.
 *
 * Touch-first counterpart of src/pages/admin/DapPolicyManagerPage.tsx. A DISTINCT
 * presentation (dense card list + facet chips + status pills + bottom-sheet detail
 * + edit sheet) over the SAME data/logic layer only:
 *   - reads via apiDb (dap_policy + a dap_condition / dap_snapshot roll-up for a
 *     scannable, mobile-friendly summary of the desktop policy/condition editor
 *     and its restorable snapshots),
 *   - the scalar edits (name, action, batch controls, rollback window, active)
 *     write through the Common Routine (routedUpdate) exactly like the desktop
 *     page — §2 single write path; never a direct apiDb write,
 *   - Dry Run (dap_simulate) and Execute (dap_run_policy) are gateway RPCs, same
 *     as the desktop — reads/operations through apiDb, not business writes.
 *
 * dap_policy is a technical config table: internal_code + entity_name are natural
 * identifiers (a policy code and the table it retains on), and action /
 * condition_mode / operator are CHECK enums — so there is no _tl lookup to
 * resolve, but every displayed enum label goes through i18n labelled constants,
 * never a bare stored literal (§17.B11). Y/N renders as status pills (§17.B10);
 * dates through formatDateTime/formatDate (§11).
 *
 * Authoring the structured conditions, raw predicate and anonymize map — and the
 * rarely-needed destructive snapshot rollback — stay on the desktop (shown
 * read-only here, mirroring MobileUserEditSheet's read-only roles). §16: never
 * the desktop grid re-hosted.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Search, Inbox, Power, X, SquarePen, Archive, FlaskConical, Play } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { routedUpdate } from '../../lib/routed_writes';
import { useFormatDate, useFormatDateTime } from '../../lib/format_date';
import {
  MobilePageHeader, MobileContentCard, MobileCardRow, MobileDetailRow, MobileFilterChips,
  MobileStatusPill, type MobileLeadTone,
} from '../components';
import type { AuthSession } from '../../types/auth';
import type { MobilePageComponentProps } from '../mobile_page_components';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'DAP.POLICIES';

type Action = 'COLD_STORAGE' | 'PURGE' | 'ANONYMIZE';
type ConditionMode = 'STRUCTURED' | 'RAW';
type Operator =
  | 'GT' | 'GE' | 'LT' | 'LE' | 'EQ' | 'NEQ'
  | 'OLDER_THAN_DAYS' | 'NEWER_THAN_DAYS' | 'IS_NULL' | 'NOT_NULL';

// §17.B11: CHECK-enum labels via i18n labelled constants — never a bare literal.
const ACTION_OPTIONS: { value: Action; key: string; fallback: string }[] = [
  { value: 'COLD_STORAGE', key: 'mobile.dap.action.COLD_STORAGE', fallback: 'Cold storage' },
  { value: 'PURGE', key: 'mobile.dap.action.PURGE', fallback: 'Purge' },
  { value: 'ANONYMIZE', key: 'mobile.dap.action.ANONYMIZE', fallback: 'Anonymize' },
];

const CONDITION_MODE_OPTIONS: { value: ConditionMode; key: string; fallback: string }[] = [
  { value: 'STRUCTURED', key: 'mobile.dap.mode.STRUCTURED', fallback: 'Structured' },
  { value: 'RAW', key: 'mobile.dap.mode.RAW', fallback: 'Raw SQL' },
];

const OPERATOR_OPTIONS: { value: Operator; key: string; fallback: string }[] = [
  { value: 'GT', key: 'mobile.dap.op.GT', fallback: '>' },
  { value: 'GE', key: 'mobile.dap.op.GE', fallback: '≥' },
  { value: 'LT', key: 'mobile.dap.op.LT', fallback: '<' },
  { value: 'LE', key: 'mobile.dap.op.LE', fallback: '≤' },
  { value: 'EQ', key: 'mobile.dap.op.EQ', fallback: '=' },
  { value: 'NEQ', key: 'mobile.dap.op.NEQ', fallback: '≠' },
  { value: 'OLDER_THAN_DAYS', key: 'mobile.dap.op.OLDER_THAN_DAYS', fallback: 'older than (days)' },
  { value: 'NEWER_THAN_DAYS', key: 'mobile.dap.op.NEWER_THAN_DAYS', fallback: 'newer than (days)' },
  { value: 'IS_NULL', key: 'mobile.dap.op.IS_NULL', fallback: 'is null' },
  { value: 'NOT_NULL', key: 'mobile.dap.op.NOT_NULL', fallback: 'is not null' },
];

function actionLabel(t: TFunction, value: string): string {
  const opt = ACTION_OPTIONS.find((o) => o.value === value);
  return opt ? t(opt.key, opt.fallback) : value;
}

function modeLabel(t: TFunction, value: ConditionMode): string {
  const opt = CONDITION_MODE_OPTIONS.find((o) => o.value === value);
  return opt ? t(opt.key, opt.fallback) : value;
}

function operatorLabel(t: TFunction, value: string): string {
  const opt = OPERATOR_OPTIONS.find((o) => o.value === value);
  return opt ? t(opt.key, opt.fallback) : value;
}

/** Lead-swatch tone: inactive is muted; active leans on the action's risk. */
function leadTone(row: MobilePolicyRow): MobileLeadTone {
  if (row.is_active !== 'Y') return 'slate';
  if (row.action === 'PURGE') return 'red';
  if (row.action === 'ANONYMIZE') return 'amber';
  return 'blue';
}

interface MobileConditionRow {
  pk: number;
  column_name: string;
  operator: Operator;
  value: string | null;
  sort_order: number;
}

interface MobileSnapshotRow {
  pk: number;
  action: string;
  row_count: number;
  created_at: string;
  expires_at: string | null;
}

interface MobilePolicyRow {
  pk: number;
  internal_code: string;
  name: string;
  entity_name: string;
  pk_column: string;
  action: Action;
  condition_mode: ConditionMode;
  raw_predicate: string | null;
  batch_size: number;
  max_batches_per_run: number;
  rollback_window_days: number;
  is_active: 'Y' | 'N';
  last_run_at: string | null;
  conditions: MobileConditionRow[];
  snapshots: MobileSnapshotRow[];
}

type StatusFacet = 'all' | 'active' | 'inactive';
type ActionFacet = 'all' | Action;

/** Compact, human summary of a policy's conditions (no raw JSON/SQL on a phone). */
function conditionsSummary(t: TFunction, p: MobilePolicyRow): string {
  if (p.condition_mode === 'RAW') return t('mobile.dap.raw_predicate', 'Raw SQL predicate');
  if (p.conditions.length === 0) return t('mobile.dap.conditions_none', 'No conditions');
  return t('mobile.dap.conditions_count', {
    count: p.conditions.length,
    defaultValue: `${p.conditions.length} condition(s)`,
  });
}

export function MobileDapPolicyManagerPage({ session }: MobilePageComponentProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const { t } = useTranslation();
  const formatDateTime = useFormatDateTime();
  const formatDate = useFormatDate();
  const [policies, setPolicies] = useState<MobilePolicyRow[]>([]);
  const [filter, setFilter] = useState('');
  const [statusFacet, setStatusFacet] = useState<StatusFacet>('active');
  const [actionFacet, setActionFacet] = useState<ActionFacet>('all');
  const [selected, setSelected] = useState<MobilePolicyRow | null>(null);
  const [editing, setEditing] = useState<MobilePolicyRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Result of a Dry Run / Execute for the open policy — a primary result the
  // admin explicitly requested, shown as data (not a courtesy notice; §17.B7).
  const [result, setResult] = useState<string | null>(null);
  const [confirmRun, setConfirmRun] = useState(false);

  const reload = async (): Promise<MobilePolicyRow[] | null> => {
    setBusy(true);
    setError(null);
    try {
      const { data: rawPolicies, error: pErr } = await apiDb
        .from('dap_policy')
        .select('dap_policy_id, internal_code, name, entity_name, pk_column, action, condition_mode, raw_predicate, batch_size, max_batches_per_run, rollback_window_days, is_active, last_run_at')
        .order('internal_code');
      if (pErr) throw pErr;

      // Roll up structured conditions per policy — the count on the card, the
      // full (read-only) list in the detail sheet.
      const { data: rawConds, error: cErr } = await apiDb
        .from('dap_condition')
        .select('dap_condition_id, dap_policy_id, column_name, operator, value, sort_order')
        .order('sort_order');
      if (cErr) throw cErr;

      // Roll up restorable snapshots (is_restored='N') — mirrors the desktop's
      // restorable-snapshots section, read-only on mobile.
      const { data: rawSnaps, error: sErr } = await apiDb
        .from('dap_snapshot')
        .select('dap_snapshot_id, dap_policy_id, action, row_count, created_at, expires_at, is_restored')
        .eq('is_restored', 'N')
        .order('created_at', { ascending: false });
      if (sErr) throw sErr;

      type RawCond = { dap_condition_id: number; dap_policy_id: number; column_name: string; operator: Operator; value: string | null; sort_order: number };
      const condsByPolicy: Record<number, MobileConditionRow[]> = {};
      for (const c of (rawConds ?? []) as RawCond[]) {
        const list = condsByPolicy[c.dap_policy_id] ?? (condsByPolicy[c.dap_policy_id] = []);
        list.push({ pk: c.dap_condition_id, column_name: c.column_name, operator: c.operator, value: c.value, sort_order: c.sort_order });
      }

      type RawSnap = { dap_snapshot_id: number; dap_policy_id: number; action: string; row_count: number; created_at: string; expires_at: string | null };
      const snapsByPolicy: Record<number, MobileSnapshotRow[]> = {};
      for (const s of (rawSnaps ?? []) as RawSnap[]) {
        const list = snapsByPolicy[s.dap_policy_id] ?? (snapsByPolicy[s.dap_policy_id] = []);
        list.push({ pk: s.dap_snapshot_id, action: s.action, row_count: s.row_count, created_at: s.created_at, expires_at: s.expires_at });
      }

      type RawPolicy = {
        dap_policy_id: number; internal_code: string; name: string | null; entity_name: string; pk_column: string;
        action: Action; condition_mode: ConditionMode; raw_predicate: string | null;
        batch_size: number; max_batches_per_run: number; rollback_window_days: number;
        is_active: 'Y' | 'N'; last_run_at: string | null;
      };
      const list: MobilePolicyRow[] = ((rawPolicies ?? []) as RawPolicy[]).map((p) => ({
        pk: p.dap_policy_id,
        internal_code: p.internal_code,
        name: p.name ?? '',
        entity_name: p.entity_name,
        pk_column: p.pk_column,
        action: p.action,
        condition_mode: p.condition_mode,
        raw_predicate: p.raw_predicate,
        batch_size: p.batch_size,
        max_batches_per_run: p.max_batches_per_run,
        rollback_window_days: p.rollback_window_days,
        is_active: p.is_active,
        last_run_at: p.last_run_at,
        conditions: condsByPolicy[p.dap_policy_id] ?? [],
        snapshots: snapsByPolicy[p.dap_policy_id] ?? [],
      }));
      setPolicies(list);
      return list;
    } catch (e) {
      setError((e as Error).message);
      return null;
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { void reload(); }, []);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return policies.filter((p) => {
      if (statusFacet === 'active' && p.is_active !== 'Y') return false;
      if (statusFacet === 'inactive' && p.is_active !== 'N') return false;
      if (actionFacet !== 'all' && p.action !== actionFacet) return false;
      if (!q) return true;
      return (
        p.internal_code.toLowerCase().includes(q) ||
        p.name.toLowerCase().includes(q) ||
        p.entity_name.toLowerCase().includes(q) ||
        p.action.toLowerCase().includes(q)
      );
    });
  }, [policies, filter, statusFacet, actionFacet]);

  const statusFacets: { key: StatusFacet; label: string }[] = [
    { key: 'all', label: t('mobile.dap.all', 'All') },
    { key: 'active', label: t('common.boolean.active', 'Active') },
    { key: 'inactive', label: t('common.boolean.inactive', 'Inactive') },
  ];

  const actionFacets: { key: ActionFacet; label: string }[] = [
    { key: 'all', label: t('mobile.dap.action_all', 'All actions') },
    ...ACTION_OPTIONS.map((o) => ({ key: o.value as ActionFacet, label: t(o.key, o.fallback) })),
  ];

  const openDetail = (p: MobilePolicyRow) => {
    setSelected(p);
    setResult(null);
    setConfirmRun(false);
    setError(null);
  };

  const closeDetail = () => {
    if (actionBusy) return;
    setSelected(null);
    setResult(null);
    setConfirmRun(false);
  };

  const toggleActive = async (p: MobilePolicyRow) => {
    setActionBusy(true);
    setError(null);
    const next = p.is_active === 'Y' ? 'N' : 'Y';
    // §2: route through the Common Routine (forensic stamp + audit + hooks).
    const { error: e } = await routedUpdate(session, 'dap_policy', 'dap_policy_id', p.pk, { is_active: next });
    setActionBusy(false);
    if (e) { setError(e.message); return; }
    setSelected(null);
    setResult(null);
    setConfirmRun(false);
    void reload();
  };

  const dryRun = async (p: MobilePolicyRow) => {
    setActionBusy(true);
    setError(null);
    setResult(null);
    try {
      const { data, error: e } = await apiDb.rpc('dap_simulate', { p_policy_code: p.internal_code });
      if (e) throw e;
      const r = data as { ok: boolean; impacted_rows?: number; error_code?: string };
      if (!r?.ok) throw new Error(r?.error_code ?? 'DAP.SIMULATE_FAILED');
      setResult(t('mobile.dap.result.dry_run', '{{rows}} row(s) would be {{action}}.', {
        rows: r.impacted_rows ?? 0,
        action: actionLabel(t, p.action).toLowerCase(),
      }));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setActionBusy(false);
    }
  };

  const run = async (p: MobilePolicyRow) => {
    setActionBusy(true);
    setError(null);
    setResult(null);
    setConfirmRun(false);
    try {
      const { data, error: e } = await apiDb.rpc('dap_run_policy', { p_policy_code: p.internal_code });
      if (e) throw e;
      const r = data as { ok: boolean; rows_affected?: number; snapshot_id?: number; error_code?: string };
      if (!r?.ok) throw new Error(r?.error_code ?? 'DAP.RUN_FAILED');
      setResult(r.snapshot_id
        ? t('mobile.dap.result.executed_snapshot', 'Executed: {{rows}} row(s) — snapshot #{{snapshot}}.', { rows: r.rows_affected ?? 0, snapshot: r.snapshot_id })
        : t('mobile.dap.result.executed', 'Executed: {{rows}} row(s).', { rows: r.rows_affected ?? 0 }));
      const list = await reload();
      if (list) setSelected(list.find((x) => x.pk === p.pk) ?? null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setActionBusy(false);
    }
  };

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.dap.title', 'Retention Policies')}
        description={t('mobile.dap.description', 'Data-retention policies, their action and restorable snapshots.')}
      />

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.dap.search', 'Search retention policies')}
          aria-label={t('mobile.dap.search', 'Search retention policies')}
        />
      </div>

      <MobileFilterChips
        chips={statusFacets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => statusFacet === v}
        onSelect={(v) => setStatusFacet(v as StatusFacet)}
      />

      <MobileFilterChips
        chips={actionFacets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => actionFacet === v}
        onSelect={(v) => setActionFacet(v as ActionFacet)}
      />

      {error && !selected && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.dap.none', 'No retention policies match')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((p) => (
            <MobileCardRow
              key={p.pk}
              lead={<Archive />}
              leadTone={leadTone(p)}
              title={p.name || p.internal_code}
              subtitle={`${p.entity_name} · ${actionLabel(t, p.action)} · ${conditionsSummary(t, p)}`}
              trailing={
                <MobileStatusPill tone={p.is_active === 'Y' ? 'ok' : 'no'}>
                  {p.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                </MobileStatusPill>
              }
              chevron
              onClick={() => openDetail(p)}
            />
          ))}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={closeDetail} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.dap.detail', 'Retention policy detail')}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><Archive size={20} /></span>
              <span>
                <div className="m-nm">{selected.name || selected.internal_code}</div>
                <div className="m-em">{selected.internal_code}</div>
              </span>
            </div>

            <MobileContentCard>
              <MobileDetailRow
                label={t('mobile.dap.status', 'Status')}
                value={
                  <MobileStatusPill tone={selected.is_active === 'Y' ? 'ok' : 'no'}>
                    {selected.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                  </MobileStatusPill>
                }
              />
              <MobileDetailRow label={t('mobile.dap.entity', 'Entity')} value={selected.entity_name} />
              <MobileDetailRow label={t('mobile.dap.action_lbl', 'Action')} value={actionLabel(t, selected.action)} />
              <MobileDetailRow
                label={t('mobile.dap.conditions', 'Conditions')}
                value={`${modeLabel(t, selected.condition_mode)} · ${conditionsSummary(t, selected)}`}
              />
              <MobileDetailRow label={t('mobile.dap.batch_size', 'Batch size')} value={selected.batch_size} />
              <MobileDetailRow label={t('mobile.dap.max_batches', 'Max batches / run')} value={selected.max_batches_per_run} />
              <MobileDetailRow
                label={t('mobile.dap.rollback_window', 'Rollback window')}
                value={t('mobile.dap.days', '{{count}} days', { count: selected.rollback_window_days })}
              />
              <MobileDetailRow
                label={t('mobile.dap.last_run', 'Last run')}
                value={selected.last_run_at ? formatDateTime(selected.last_run_at) : t('common.none', 'None')}
              />
            </MobileContentCard>

            {/* Structured conditions (read-only — author on desktop). */}
            {selected.condition_mode === 'STRUCTURED' && selected.conditions.length > 0 && (
              <MobileContentCard>
                {selected.conditions.map((c) => (
                  <MobileDetailRow
                    key={c.pk}
                    label={c.column_name || t('mobile.dap.column', 'column')}
                    value={`${operatorLabel(t, c.operator)}${!['IS_NULL', 'NOT_NULL'].includes(c.operator) && c.value ? ` ${c.value}` : ''}`}
                  />
                ))}
              </MobileContentCard>
            )}

            {/* Restorable snapshots (read-only — roll back on desktop). */}
            <MobileContentCard>
              {selected.snapshots.length === 0 ? (
                <MobileDetailRow label={t('mobile.dap.snapshots', 'Snapshots')} value={t('mobile.dap.snapshots_none', 'None')} />
              ) : (
                selected.snapshots.map((s) => (
                  <MobileDetailRow
                    key={s.pk}
                    label={`#${s.pk} · ${actionLabel(t, s.action)}`}
                    value={`${t('mobile.dap.snapshot_rows', '{{count}} rows', { count: s.row_count })} · ${t('mobile.dap.snapshot_exp', 'exp {{date}}', { date: s.expires_at ? formatDate(s.expires_at) : '—' })}`}
                  />
                ))
              )}
            </MobileContentCard>

            {/* Dry Run / Execute (gateway RPCs). Execute is gated behind an
                explicit confirm — a destructive throttled run on a phone. */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, padding: '6px 14px 0' }}>
              <button type="button" className="m-btn m-btn-ghost" disabled={actionBusy} onClick={() => void dryRun(selected)}>
                <FlaskConical size={16} /> {t('mobile.dap.dry_run', 'Dry run')}
              </button>
              <button type="button" className="m-btn m-btn-danger" disabled={actionBusy} onClick={() => setConfirmRun(true)}>
                <Play size={16} /> {t('mobile.dap.execute', 'Execute')}
              </button>
            </div>

            {confirmRun && (
              <div className="m-fieldcard">
                <div className="m-field">
                  <span className="m-k">{t('mobile.dap.confirm', 'Confirm')}</span>
                  <span className="m-v">
                    {t('mobile.dap.confirm_run', 'Execute {{action}} on {{entity}}? Throttled batches run now.', {
                      action: actionLabel(t, selected.action),
                      entity: selected.entity_name,
                    })}
                  </span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, padding: '6px 14px 0' }}>
                  <button type="button" className="m-btn m-btn-danger" disabled={actionBusy} onClick={() => void run(selected)}>
                    {actionBusy ? t('mobile.dap.working', 'Working…') : t('mobile.dap.confirm_execute', 'Yes, execute')}
                  </button>
                  <button type="button" className="m-btn m-btn-ghost" disabled={actionBusy} onClick={() => setConfirmRun(false)}>
                    {t('common.cancel', 'Cancel')}
                  </button>
                </div>
              </div>
            )}

            {result && (
              <MobileContentCard>
                <MobileDetailRow label={t('mobile.dap.result_label', 'Result')} value={result} />
              </MobileContentCard>
            )}

            {error && (
              <MobileContentCard><div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div></MobileContentCard>
            )}

            <div className="m-actionbar">
              {canChange && (<button
                type="button"
                className="m-btn m-btn-primary"
                disabled={actionBusy}
                onClick={() => { setEditing(selected); setSelected(null); setResult(null); setConfirmRun(false); }}
              >
                <SquarePen size={16} />
                {t('mobile.dap.edit', 'Edit')}
              </button>)}
              {canChange && (<button
                type="button"
                className={'m-btn ' + (selected.is_active === 'Y' ? 'm-btn-danger' : 'm-btn-ghost')}
                disabled={actionBusy}
                onClick={() => void toggleActive(selected)}
              >
                <Power size={16} />
                {selected.is_active === 'Y' ? t('users.list.actions.deactivate', 'Deactivate') : t('users.list.actions.activate', 'Activate')}
              </button>)}
              <button type="button" className="m-btn m-btn-ghost" disabled={actionBusy} onClick={closeDetail} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>

      <MobileDapPolicyEditSheet
        session={session}
        policy={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); void reload(); }}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Edit sheet — the scalar policy fields a phone can safely edit (Name, Action,
// Batch size, Max batches/run, Rollback window, Active), saved through the
// Common Routine (routedUpdate) — §2. Entity, PK column, condition mode, the
// structured conditions, raw predicate and anonymize map stay on the desktop
// and are shown read-only here (mirrors MobileUserEditSheet's read-only roles).
// ---------------------------------------------------------------------------

interface MobileDapPolicyEditSheetProps {
  session: AuthSession;
  policy: MobilePolicyRow | null;
  onClose: () => void;
  onSaved: () => void;
}

/** Parse a numeric input, falling back to a previous value when left blank. */
function toIntOr(value: string, fallback: number): number {
  const n = Number.parseInt(value, 10);
  return Number.isNaN(n) || n < 0 ? fallback : n;
}

function MobileDapPolicyEditSheet({ session, policy, onClose, onSaved }: MobileDapPolicyEditSheetProps) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [action, setAction] = useState<Action>('COLD_STORAGE');
  const [batchSize, setBatchSize] = useState('1000');
  const [maxBatches, setMaxBatches] = useState('50');
  const [windowDays, setWindowDays] = useState('30');
  const [active, setActive] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Seed the form whenever a different policy is opened.
  useEffect(() => {
    if (!policy) return;
    setName(policy.name);
    setAction(policy.action);
    setBatchSize(String(policy.batch_size));
    setMaxBatches(String(policy.max_batches_per_run));
    setWindowDays(String(policy.rollback_window_days));
    setActive(policy.is_active === 'Y');
    setError(null);
  }, [policy]);

  const save = async () => {
    if (!policy) return;
    setBusy(true);
    setError(null);
    // §2: route through the Common Routine (forensic stamp + audit + hooks).
    const { error: e } = await routedUpdate(session, 'dap_policy', 'dap_policy_id', policy.pk, {
      name: name.trim(),
      action,
      batch_size: toIntOr(batchSize, policy.batch_size),
      max_batches_per_run: toIntOr(maxBatches, policy.max_batches_per_run),
      rollback_window_days: toIntOr(windowDays, policy.rollback_window_days),
      is_active: active ? 'Y' : 'N',
    });
    setBusy(false);
    if (e) { setError(e.message); return; }
    onSaved();
  };

  const open = policy !== null;

  return (
    <>
      <div className={'m-scrim' + (open ? ' m-open' : '')} onClick={() => { if (!busy) onClose(); }} />
      <div className={'m-sheet' + (open ? ' m-open' : '')} aria-label={t('mobile.dap.edit_sheet', 'Edit retention policy')}>
        <div className="m-grabber" />
        {policy && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><Archive size={20} /></span>
              <span>
                <div className="m-nm">{t('mobile.dap.edit_title', 'Edit policy')}</div>
                <div className="m-em">{policy.internal_code}</div>
              </span>
            </div>

            <div className="m-fieldcard">
              <div className="m-formrow">
                <label>{t('mobile.dap.entity', 'Entity')}</label>
                <div className="m-formrow-ro">
                  {policy.entity_name} · {policy.pk_column}
                  <span className="m-formrow-hint"> · {t('mobile.dap.desktop_only', 'edit on desktop')}</span>
                </div>
              </div>
              <div className="m-formrow">
                <label htmlFor="m-dap-name">{t('mobile.dap.name', 'Name')}</label>
                <input
                  id="m-dap-name"
                  className="m-inp"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="m-formrow">
                <label htmlFor="m-dap-action">{t('mobile.dap.action_lbl', 'Action')}</label>
                <select
                  id="m-dap-action"
                  className="m-sel"
                  value={action}
                  onChange={(e) => setAction(e.target.value as Action)}
                >
                  {ACTION_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{t(o.key, o.fallback)}</option>
                  ))}
                </select>
              </div>
              <div className="m-formrow">
                <label htmlFor="m-dap-batch">{t('mobile.dap.batch_size', 'Batch size')}</label>
                <input
                  id="m-dap-batch"
                  className="m-inp"
                  type="number"
                  inputMode="numeric"
                  value={batchSize}
                  onChange={(e) => setBatchSize(e.target.value)}
                />
              </div>
              <div className="m-formrow">
                <label htmlFor="m-dap-maxb">{t('mobile.dap.max_batches', 'Max batches / run')}</label>
                <input
                  id="m-dap-maxb"
                  className="m-inp"
                  type="number"
                  inputMode="numeric"
                  value={maxBatches}
                  onChange={(e) => setMaxBatches(e.target.value)}
                />
              </div>
              <div className="m-formrow">
                <label htmlFor="m-dap-window">{t('mobile.dap.rollback_window', 'Rollback window (days)')}</label>
                <input
                  id="m-dap-window"
                  className="m-inp"
                  type="number"
                  inputMode="numeric"
                  value={windowDays}
                  onChange={(e) => setWindowDays(e.target.value)}
                />
              </div>
              <div className="m-formrow m-formrow-inline">
                <span>
                  <div className="m-formrow-l">{t('common.boolean.active', 'Active')}</div>
                  <div className="m-formrow-s">{t('mobile.dap.active_hint', 'Policy is eligible to run')}</div>
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={active}
                  className={'m-switch ' + (active ? 'm-on' : 'm-off')}
                  onClick={() => setActive((v) => !v)}
                >
                  <span className="m-knob" />
                </button>
              </div>
              <div className="m-formrow">
                <label>{t('mobile.dap.conditions', 'Conditions')}</label>
                <div className="m-formrow-ro">
                  {conditionsSummary(t, policy)}
                  <span className="m-formrow-hint"> · {t('mobile.dap.desktop_only', 'edit on desktop')}</span>
                </div>
              </div>
            </div>

            {error && <div className="m-card"><div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div></div>}

            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-primary" disabled={busy} onClick={() => void save()}>
                {busy ? t('common.saving', 'Saving…') : t('common.save', 'Save changes')}
              </button>
              <button type="button" className="m-btn m-btn-ghost" disabled={busy} onClick={onClose} aria-label={t('common.cancel', 'Cancel')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
